import { lazy } from 'react';

// Detect a failed dynamic-import / chunk-load error (across browsers).
export function isChunkLoadError(err) {
  if (!err) return false;
  const name = err.name || '';
  const msg = err.message || String(err) || '';
  return name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /'?text\/html'? is not a valid JavaScript MIME type/i.test(msg) ||
    /Importing a module script failed/i.test(msg);
}

// Robust self-heal for stale-tab chunk errors. Replaces the old one-shot
// (`chunk_reload_once`) guard, which allowed a SINGLE reload per browser
// session — so on a day with many deploys a long-lived tab ran out of reloads
// and got stuck on the error screen forever. This uses a rolling window: it can
// reload many times across a session (so every deploy keeps self-healing) but
// caps reloads inside a short window so a genuinely broken build can't hard-loop.
//
// Returns true if a reload was triggered, false if it declined (cooldown/cap) —
// callers fall through to the manual "reload" button when it returns false.
export function attemptChunkReload() {
  try {
    const KEY = 'chunk_reloads_v2';
    const WINDOW_MS = 60000;   // rolling look-back window
    const MAX_IN_WINDOW = 3;   // at most 3 auto-reloads per window, then give up
    const COOLDOWN_MS = 4000;  // min gap between reloads (debounces double-fires)
    const now = Date.now();
    let times = [];
    try { times = JSON.parse(sessionStorage.getItem(KEY) || '[]'); } catch { times = []; }
    times = times.filter((t) => typeof t === 'number' && now - t < WINDOW_MS);
    const last = times.length ? times[times.length - 1] : 0;
    if (now - last < COOLDOWN_MS) return false;      // just reloaded — let it settle
    if (times.length >= MAX_IN_WINDOW) return false; // looping on a broken build — stop
    times.push(now);
    try { sessionStorage.setItem(KEY, JSON.stringify(times)); } catch { /* ignore */ }
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

// React.lazy with retry. A dynamic import can fail transiently (network blip,
// or a cold-cache miss the first time a freshly-deployed chunk is fetched — the
// origin serves /static through the Express app, so the very first fetch of a new
// chunk hash can stall/reset before Cloudflare has it cached) or because a deploy
// swapped the chunk hash out from under an open tab.
//
// Why re-calling import() actually re-fetches: webpack 5 deletes a failed chunk
// from its internal installedChunks registry on script onerror, so the NEXT
// __webpack_require__.e() (i.e. a retried import()) genuinely re-downloads it
// rather than replaying the cached rejection. That's what makes this loop work.
//
// We retry several times with EXPONENTIAL backoff so a multi-second cold-fetch
// hiccup self-heals WITHOUT a jarring full-page reload. The old 2×350ms (~0.7s
// total) gave up too fast and fell through to the capped ErrorBoundary reload,
// which is exactly what left the Notificaciones route stuck. Only after we've
// exhausted the patient retries do we reject → ErrorBoundary pulls a fresh
// index.html + chunks. Non-chunk errors reject immediately (real bugs, not blips).
export default function lazyRetry(factory, retries = 5, baseDelayMs = 400, maxDelayMs = 5000) {
  return lazy(() => new Promise((resolve, reject) => {
    const attempt = (n) => {
      factory()
        .then(resolve)
        .catch((err) => {
          if (n >= retries || !isChunkLoadError(err)) { reject(err); return; }
          // 400, 800, 1600, 3200, 5000(cap) — ~11s of patient retrying worst case.
          const delay = Math.min(baseDelayMs * Math.pow(2, n), maxDelayMs);
          setTimeout(() => attempt(n + 1), delay);
        });
    };
    attempt(0);
  }));
}
